import cn from 'classnames'
import {useField, useFormikContext} from 'formik'
import {FC, HTMLProps, useEffect, useRef} from 'react'

import {HOVER_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'

import {TextVariant} from '../common/content/Text'

type TextAreaProps = {
  name: string
  value?: string
  numberOfRows?: number
  hasError?: boolean
  autosize?: boolean
  textVariant?: TextVariant
  setHeight?: (height: number) => void
  maxHeight?: string
  focusOnRender?: boolean
  paddingRight?: string
} & HTMLProps<HTMLTextAreaElement>

export const TextArea: FC<TextAreaProps> = ({
  name,
  value,
  numberOfRows = 3,
  hasError = false,
  autosize = false,
  textVariant = TextVariant.text16,
  setHeight,
  maxHeight,
  focusOnRender = false,
  paddingRight = theme.spacing[2.5],
  ...props
}) => {
  const [field] = useField<string>(name)
  const {isSubmitting} = useFormikContext()

  const isDisabled = isSubmitting || props.disabled

  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const rowHeight = textVariant === TextVariant.text16 ? 30 : 26

  useEffect(() => {
    if (autosize && textAreaRef.current) {
      textAreaRef.current.style.height = numberOfRows * rowHeight + 'px'
      const scrollHeight = textAreaRef.current.scrollHeight
      textAreaRef.current.style.height = scrollHeight + 'px'
      setHeight && setHeight(scrollHeight)
    }
  }, [autosize, field.value, setHeight, numberOfRows, rowHeight])

  useEffect(() => {
    if (focusOnRender) {
      setTimeout(() => textAreaRef.current?.focus(), HOVER_TRANSITION)
    }
  }, [focusOnRender])

  return (
    <>
      <textarea
        {...field}
        {...props}
        ref={textAreaRef}
        onChange={props.onChange || field.onChange}
        value={value || field.value || ''}
        disabled={isDisabled}
        rows={numberOfRows}
        className={cn('textarea', hasError && 'error', isDisabled && 'disabled')}
      />

      <style jsx>{`
        .textarea {
          width: 100%;
          ${theme.typography[textVariant].desktop};
          color: ${theme.palette.text.default};
          padding: ${theme.spacing[2]} 12px;
          padding-right: ${paddingRight};
          border-radius: 8px;
          background-color: ${theme.palette.background.default};
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[300]};
          vertical-align: top;
          resize: none;
          max-height: ${maxHeight};
        }

        .textarea:not(.error, .disabled):focus {
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[900]},
            0px 0px 0px 4px ${theme.palette.gray[100]};
        }

        .error {
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.red[700]};
        }

        .error:focus {
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.error.main},
            0px 0px 0px 2px ${theme.palette.red[200]};
        }

        .disabled {
          color: ${theme.palette.gray[400]};
          background-color: ${theme.palette.gray[200]};
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[200]};
        }

        ::-webkit-scrollbar-track {
          width: 4px;
          background-color: 'transparent';
        }

        ::-webkit-scrollbar {
          width: 4px;
          background-color: 'transparent';
        }

        ::-webkit-scrollbar-thumb {
          width: 4px;
          height: 40px;
          border-radius: 8px;
          background-color: ${theme.palette.gray[300]};
        }

        .textarea::placeholder {
          ${theme.typography[textVariant].desktop};
        }
      `}</style>
    </>
  )
}
